import { CircleNotch } from '@phosphor-icons/react/ssr';
import { Slot } from 'radix-ui';
import {
  cloneElement,
  type ComponentProps,
  type ElementType,
  isValidElement,
  type ReactNode,
} from 'react';

import { cn } from '../../lib/utils';
import { Button as ButtonPrimitive } from '../ui/button';

export type ButtonProps = Omit<ComponentProps<'button'>, 'children'> & {
  /** Figma "Style". Primary: one per view. Danger: destructive, confirm first. */
  variant?: 'primary' | 'secondary' | 'outline' | 'ghost' | 'danger';
  /** Figma "Size": Small 32 · Medium 40 (default) · Large 48. */
  size?: 'sm' | 'md' | 'lg';
  /** Icon before the label (a Phosphor icon, or any component taking className). */
  leadingIcon?: ElementType<{ className?: string }>;
  /** Icon after the label. */
  trailingIcon?: ElementType<{ className?: string }>;
  /**
   * Figma "Loading": label and icons turn invisible but keep their space and a
   * spinner is centred, so the width never changes. Also disables the button.
   */
  loading?: boolean;
  /** Render the single child element (a link, a router Link) with button styling. */
  asChild?: boolean;
  /** Label - a verb in sentence case ("Generate variations"). */
  children?: ReactNode;
};

/**
 * Primary action trigger (Figma: Components ↳ Button). Built on the shadcn
 * Button primitive; adds the Figma anatomy: a 4px "Label wrap" around the
 * label (so icon sides get 4px less padding), optional leading and trailing
 * icons, and the loading state.
 */
export function Button({
  variant = 'primary',
  size = 'md',
  leadingIcon: LeadingIcon,
  trailingIcon: TrailingIcon,
  loading = false,
  asChild = false,
  disabled,
  className,
  children,
  ...props
}: ButtonProps) {
  // Transparent while loading (Figma: opacity 0) so the button keeps its width.
  // Not `visibility: hidden` - that would also drop the accessible name.
  const contentClass = cn(loading && 'opacity-0');
  const wrapLabel = (label: ReactNode) => (
    <span className={cn('px-1', contentClass)}>{label}</span>
  );

  // With asChild, the child (e.g. <a>) becomes the button element and its own
  // children become the label.
  const child =
    asChild && isValidElement<{ children?: ReactNode }>(children) ? children : null;

  return (
    <ButtonPrimitive
      variant={variant}
      size={size}
      asChild={asChild}
      disabled={asChild ? undefined : disabled || loading}
      aria-disabled={asChild && (disabled || loading) ? true : undefined}
      aria-busy={loading || undefined}
      className={className}
      {...props}
    >
      {loading && (
        <CircleNotch
          aria-hidden
          data-slot="spinner"
          className="absolute top-1/2 left-1/2 -translate-1/2 animate-spin [animation-duration:800ms]"
        />
      )}
      {LeadingIcon && <LeadingIcon aria-hidden className={contentClass} />}
      {child ? (
        <Slot.Slottable>
          {cloneElement(child, undefined, wrapLabel(child.props.children))}
        </Slot.Slottable>
      ) : (
        wrapLabel(children)
      )}
      {TrailingIcon && <TrailingIcon aria-hidden className={contentClass} />}
    </ButtonPrimitive>
  );
}
